import React, { useId } from 'react';
import {
    Doc31ValidationIssue,
    Doc31ValidationResult,
} from '../../../types/doc31';
import {
    describeDoc31Issue,
    describeDoc31Path,
    doc31CouponIdOf,
} from '../../../utils/doc31';
import style from './style.module.scss';

interface Props {
    /** "Ready to go live?" before launch, "Setup checks" after. */
    title: string;
    /** The check of the saved setup. */
    validation: Doc31ValidationResult;
    /** Why the last save was refused (422), checked against the edits. */
    refusal: Doc31ValidationResult | null;
    isDirty: boolean;
    onFocusField: (path: string) => void;
    onOpenCoupon: (promotionId: number) => void;
}

export const ReadinessPanel = ({
    title,
    validation,
    refusal,
    isDirty,
    onFocusField,
    onOpenCoupon,
}: Props) => {
    // Both tab panes may be mounted at once, each with its own panel.
    const titleId = useId();
    const { errors, warnings } = refusal ?? validation;
    let note: React.ReactNode = null;
    if (refusal) {
        note = (
            <p className={style.panel_refused}>
                Not saved. Fix these, then save again.
            </p>
        );
    } else if (isDirty) {
        note = (
            <p className={style.panel_note}>
                Checks the saved setup. Save to recheck.
            </p>
        );
    }

    const renderIssue = (
        issue: Doc31ValidationIssue,
        tone: 'error' | 'warning',
        index: number
    ) => {
        const couponId = doc31CouponIdOf(issue.path);
        const isCouponList =
            couponId === null &&
            issue.path.split('.')[0] === 'couponPromotions';
        const body = (
            <>
                <span
                    aria-hidden
                    className={
                        tone === 'error' ? style.mark_error : style.mark_warning
                    }
                >
                    {tone === 'error' ? '✕' : '!'}
                </span>
                <span className={style.issue_text}>
                    <strong>{describeDoc31Path(issue.path)}</strong>
                    <span>{describeDoc31Issue(issue)}</span>
                    {isCouponList && (
                        <span className={style.issue_hint}>
                            Add or activate a coupon in Coupons.
                        </span>
                    )}
                </span>
            </>
        );
        return (
            <li key={`${tone}-${index}`}>
                {isCouponList ? (
                    <div className={style.issue}>{body}</div>
                ) : (
                    <button
                        type="button"
                        className={`${style.issue} ${style.issue_link}`}
                        onClick={() =>
                            couponId === null
                                ? onFocusField(issue.path)
                                : onOpenCoupon(couponId)
                        }
                    >
                        {body}
                    </button>
                )}
            </li>
        );
    };

    return (
        <aside className={style.panel} aria-labelledby={titleId}>
            <h2 id={titleId} className={style.panel_title}>
                {title}
            </h2>
            <p className={style.panel_count}>
                {errors.length === 0 && warnings.length === 0 ? (
                    <span className={style.count_ok}>All set</span>
                ) : (
                    <>
                        {errors.length > 0 && (
                            <span className={style.count_error}>
                                {errors.length} to fix
                            </span>
                        )}
                        {errors.length > 0 && warnings.length > 0 && ' · '}
                        {warnings.length > 0 && (
                            <span className={style.count_warning}>
                                {warnings.length} to check
                            </span>
                        )}
                    </>
                )}
            </p>
            {note}
            {(errors.length > 0 || warnings.length > 0) && (
                <ul className={style.issues}>
                    {errors.map((issue, index) =>
                        renderIssue(issue, 'error', index)
                    )}
                    {warnings.map((issue, index) =>
                        renderIssue(issue, 'warning', index)
                    )}
                </ul>
            )}
        </aside>
    );
};
